import { productAnalytics, useDebouncedSave, useMutateSoilSightOutcomes, useReplaceSoilSightCropYears } from "utils";

import { toApiCropYear } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import { classifyForecastError } from "pages/app/scenario-analysis/utils/soilSightTracking";
import { useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";

const RECALC_DEBOUNCE_MS = 500;

/**
 * Step-3 recalc-on-change: debounces change-scenario edits, persists the rows, then
 * re-runs the calculation. The request id drops stale responses, since slow /run calls can resolve
 * out of order.
 */
export const useChangeScenarioRecalc = () => {
  const { changeCropYears, scenarioRefs, setOutcomes, setCalcStatus } = useScenarioOnboarding();
  const { mutateAsync: replaceCropYearsAsync } = useReplaceSoilSightCropYears();
  const { mutateAsync: mutateOutcomesAsync } = useMutateSoilSightOutcomes();

  const { projectId, changeScenarioId } = scenarioRefs;

  useDebouncedSave({
    value: changeCropYears,
    delay: RECALC_DEBOUNCE_MS,
    rearmOn: [projectId, changeScenarioId],
    // The rows arriving from step-1 generation are already persisted and calculated.
    initial: "skip",
    shouldSkip: (years) => !projectId || !changeScenarioId || years.length === 0,
    onSchedule: () => setCalcStatus("calculating"),
    // Leaving step 3 mid-debounce still saves the edit; calcStatus stays "calculating" until that run lands.
    flushOnUnmount: true,
    save: async (years, request) => {
      if (!projectId || !changeScenarioId) return;
      let isSaved = false;
      try {
        await replaceCropYearsAsync({
          scenarioId: changeScenarioId,
          body: { cropYears: years.map((year, index) => toApiCropYear(year, index + 1)) },
        });
        isSaved = true;
        const outcomes = await mutateOutcomesAsync(projectId);
        if (!request.isLatestEdit()) return;
        setOutcomes(outcomes);
        setCalcStatus("idle");
      } catch (recalcError) {
        if (!request.isLatestEdit()) return;
        setCalcStatus("error");
        productAnalytics.track("soilsights_onboarding:forecast_fail", {
          step_index: 3,
          error_type: isSaved ? classifyForecastError(recalcError) : "save_failed",
        });
      }
    },
  });
};
